import React, { useEffect, useRef } from "react";
import { Mic, MicOff, XIcon } from "lucide-react";

import DefaultProfile from "../../assets/images/default_profile_new.png";
import useLiveAvatar from "../../hooks/useLiveAvatar";
import { Card } from "../elevenlabs/ui/card";
import LiveWaveform from "../elevenlabs/ui/live-waveform";
import { cn } from "../ui/utils";
import type {
  LiveAvatarError,
  LiveAvatarStatus,
  LiveAvatarTranscriptMessage,
} from "./types";

export interface LiveAvatarPanelProps {
  eventId: number;
  realtimeBookingId: number;
  timezone: string;
  allocatedUserName?: string;
  autoStart?: boolean;
  debug?: boolean;
  className?: string;
  posterUrl?: string;
  onTranscript?: (message: LiveAvatarTranscriptMessage) => void;
  onStatusChange?: (status: LiveAvatarStatus) => void;
  onError?: (error: LiveAvatarError) => void;
}

const statusLabel: Record<LiveAvatarStatus, string> = {
  idle: "Tap to start",
  connecting: "Connecting...",
  connected: "Listening...",
  disconnecting: "Ending session...",
  error: "Avatar unavailable",
};

const userFacingError = (error: LiveAvatarError): string => {
  switch (error.code) {
    case "permission_denied":
      return "Microphone permission is required to use the avatar.";
    case "config_missing":
      return "Avatar is not configured for this flow.";
    case "network":
      return "Network error while starting the avatar session.";
    default:
      return error.message || "Avatar session failed to start.";
  }
};

export default function LiveAvatarPanel({
  eventId,
  realtimeBookingId,
  timezone,
  allocatedUserName,
  autoStart = true,
  debug = false,
  className,
  posterUrl,
  onTranscript,
  onStatusChange,
  onError,
}: LiveAvatarPanelProps) {
  const mediaRef = useRef<HTMLVideoElement>(null);

  const { status, error, isMuted, start, stop, toggleMute } = useLiveAvatar({
    eventId,
    realtimeBookingId,
    timezone,
    mediaRef,
    autoStart,
    debug,
    onTranscript,
    onStatusChange,
  });

  useEffect(() => {
    if (error) onError?.(error);
  }, [error, onError]);

  const isConnected = status === "connected";
  const isProcessing = status === "connecting" || status === "disconnecting";
  const canStart = status === "idle" || status === "error";
  const statusText =
    status === "error" && error
      ? userFacingError(error)
      : isConnected && isMuted
        ? "Muted"
        : statusLabel[status];

  const handlePrimaryClick = () => {
    if (canStart) {
      void start();
    } else if (isConnected) {
      void toggleMute();
    }
  };

  return (
    <Card
      className={cn(
        "h-full w-full max-w-md border-0 p-0 shadow-none",
        className,
      )}
    >
      <div className="flex h-full flex-col items-center justify-between">
        <div className="mt-8 text-center text-base font-bold text-foreground md:mt-2">
          Hey! this is {allocatedUserName || "Anna"}, how can I help you today?
        </div>

        <div className="mt-8 h-24 w-24 overflow-hidden rounded-full bg-muted">
          <video
            ref={mediaRef}
            className="h-full w-full object-cover"
            autoPlay
            playsInline
            poster={posterUrl || DefaultProfile}
            aria-label="Live avatar video"
          />
        </div>

        <div className="mt-8 flex w-full items-center justify-center px-4">
          <LiveWaveform
            active={false}
            processing={isProcessing}
            idlePreview={isConnected && !isMuted}
            mode="static"
            height={60}
            barWidth={3}
            barGap={3}
            barRadius={999}
            barColor="#BDBDBD"
            fadeEdges={false}
            sensitivity={1.35}
            smoothingTimeConstant={0.85}
          />
        </div>

        <div
          className={cn(
            "mt-6 px-4 text-center text-sm text-muted-foreground",
            status === "error" && "text-destructive",
          )}
        >
          {statusText}
        </div>

        <div className="mt-8 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={handlePrimaryClick}
            disabled={isProcessing}
            className={cn(
              "flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-0 bg-white p-2 shadow-2xl",
              isProcessing && "cursor-not-allowed opacity-50",
            )}
            aria-label={
              isConnected
                ? isMuted
                  ? "Unmute avatar"
                  : "Mute avatar"
                : status === "error"
                  ? "Retry avatar"
                  : "Start avatar"
            }
          >
            {isConnected && isMuted ? (
              <MicOff className="h-6 w-6" />
            ) : (
              <Mic className="h-6 w-6" />
            )}
          </button>

          <button
            type="button"
            onClick={() => void stop()}
            disabled={!isConnected}
            className={cn(
              "flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-0 bg-red-500 p-2 text-white shadow",
              !isConnected && "cursor-not-allowed opacity-50",
            )}
            aria-label="End session"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
    </Card>
  );
}
